<template>
  <div class="table-config">
    <div class="table-config__header"><p>Modifica tabella</p></div>
    <div class="table-config__content">
      <p class="table-config__title">Visualizza o nascondi le colonne</p>
      <p class="table-config__hint">
        <i class="el-icon-info" />
        {{ $t('table.columnsConfig.stickyHint', { max: maxStickyColumns }) }}
      </p>
      <div class="table-config__columns">
        <el-checkbox-group
          v-model="selectedColumns"
          class="table-config__group"
        >
          <div
            v-for="(column, index) in filteredColumns"
            :key="index"
            class="table-config__item"
          >
            <el-checkbox :label="column">{{
              $t(`filters.${column}`)
            }}</el-checkbox>
            <button
              type="button"
              class="table-config__pin"
              :class="{ 'table-config__pin--active': isSticky(column) }"
              :disabled="!canToggleSticky(column)"
              :title="
                isSticky(column)
                  ? $t('table.columnsConfig.unpin')
                  : $t('table.columnsConfig.pin')
              "
              @click="toggleSticky(column)"
            >
              <i class="fas fa-thumbtack"></i>
            </button>
          </div>
        </el-checkbox-group>
      </div>

      <div>
        <el-button
          type="default"
          @click="$emit('close')"
          >Annulla</el-button
        >
        <el-button
          type="primary"
          @click="saveConfig"
          >Salva</el-button
        >
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';

  import { usersApi } from '@/api';

  const MAX_STICKY_COLUMNS = 3;

  export default {
    name: 'TableColumnsConfig',
    props: {
      tableKey: { type: String, required: true },
      availableColumns: { type: Object, required: true },
    },
    data() {
      return {
        selectedColumns: [],
        selectedStickyColumns: [],
        maxStickyColumns: MAX_STICKY_COLUMNS,
      };
    },
    computed: {
      ...mapGetters(['tablesStatus', 'enabledColumns', 'stickyColumns']),
      filteredColumns() {
        return Object.keys(this.availableColumns).reduce((columns, key) => {
          if (this.availableColumns[key].type !== 'actions') {
            return [...columns, this.availableColumns[key].label];
          }
          return columns;
        }, []);
      },
    },
    watch: {
      selectedColumns(columns) {
        this.selectedStickyColumns = this.selectedStickyColumns.filter(
          (column) => columns.includes(column)
        );
      },
    },
    methods: {
      ...mapActions(['updateTableColumnsConfig', 'updateTableStickyColumns']),
      isSticky(column) {
        return this.selectedStickyColumns.includes(column);
      },
      canToggleSticky(column) {
        if (!this.selectedColumns.includes(column)) {
          return false;
        }
        return (
          this.isSticky(column) ||
          this.selectedStickyColumns.length < MAX_STICKY_COLUMNS
        );
      },
      toggleSticky(column) {
        this.selectedStickyColumns = this.isSticky(column)
          ? this.selectedStickyColumns.filter((item) => item !== column)
          : [...this.selectedStickyColumns, column];
      },
      async saveConfig() {
        this.updateTableColumnsConfig({
          tableKey: this.tableKey,
          config: this.selectedColumns,
        });
        this.updateTableStickyColumns({
          tableKey: this.tableKey,
          config: this.selectedStickyColumns,
        });
        const globalColumnsConfig = Object.keys(this.tablesStatus).reduce(
          (acc, key) => {
            const columnConfig = {
              enabledColumns: this.tablesStatus[key].enabledColumns,
              stickyColumns: this.tablesStatus[key].stickyColumns || [],
            };
            return { ...acc, [key]: columnConfig };
          },
          {}
        );
        await usersApi.patchMe({
          metadata: globalColumnsConfig,
        });
        this.$emit('close');
      },
    },
    created() {
      const enabledColumns = this.enabledColumns(this.tableKey);
      if (enabledColumns.length) {
        this.selectedColumns = [...enabledColumns];
      } else {
        this.selectedColumns = [...this.filteredColumns];
      }
      this.selectedStickyColumns = this.stickyColumns(this.tableKey).filter(
        (column) => this.selectedColumns.includes(column)
      );
    },
  };
</script>

<style lang="scss">
  .table-config {
    position: fixed;
    top: 0;
    right: 0;
    width: 20vw;
    height: 100vh;
    overflow-y: auto;
    background: $--color-black;
    z-index: 10000;
    box-shadow: $--box-shadow-base;

    &__header {
      color: $--color-white;
      background-color: $--color-black-lighter;
      font-size: 0.87rem;
      padding: 0.6rem 1rem;
    }

    &__content {
      padding: 1rem;
    }

    &__title {
      color: $--color-white;
      font-weight: 700;
      font-size: 0.87rem;
    }

    &__hint {
      display: flex;
      align-items: flex-start;
      gap: 6px;
      color: $--color-text-regular;
      font-size: 0.75rem;
      line-height: 1.4;
      margin-top: 0.6rem;
      padding: 0.5rem 0.6rem;
      border-radius: 6px;
      background: rgba($--color-primary, 0.1);
      border: 1px solid rgba($--color-primary, 0.25);

      .el-icon-info {
        color: $--color-primary;
        font-size: 0.87rem;
        line-height: 1.4;
      }
    }

    &__group {
      margin: 1.5rem 0;
    }

    &__item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin: 0.5rem 0;
    }

    &__pin {
      background: none;
      border: none;
      cursor: pointer;
      padding: 0.25rem 0.4rem;
      font-size: 0.87rem;
      color: $--color-text-regular;

      &:disabled {
        cursor: not-allowed;
        opacity: 0.35;
      }

      &--active {
        color: $--color-primary;
        transform: rotate(-45deg);
      }
    }
  }
</style>
